import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import cronstrue from 'cronstrue';
import { Subject, takeUntil } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { AppService } from '../../../app.service';
import { GlobalService } from '../../../global/services/global.service';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';
import { StompMessageService } from '../../../shared/_services/stomp-message.service';
import { GoldSyncCronHelpDialogComponent } from './gold-sync-cron-help-dialog/gold-sync-cron-help-dialog.component';
import {
  GoldSyncTableDetailsDialogComponent,
  GoldSyncTableDetailsDialogData,
} from './gold-sync-table-details-dialog/gold-sync-table-details-dialog.component';
import { GoldSyncService } from './gold-sync.service';
import {
  extractGoldSyncLogHistory,
  isSchedulerCallFailure,
  isSchedulerCallSuccess,
  unwrapSchedulerResponse,
} from './gold-sync-response.util';
import { isSsoOnPrivilegedList } from '../../shared/admin-privilege.util';

import { safeJsonInput } from '../../../shared/_utils/safe-json-input';
interface SrcTypeMapping {
  fromData: string;
  detail: string;
}

@Component({
    selector: 'gtw-gold-sync',
    templateUrl: './gold-sync.component.html',
    styleUrls: ['./gold-sync.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class GoldSyncComponent implements OnInit, OnDestroy {
  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  @Input('base-urls')
  set _base_urls(v: string) {
    try {
      const u = safeJsonInput<any>(v || '{}', null, 'gold-sync.parse1');
      if (u && Object.keys(u).length > 0) {
        this.ceBaseURLs = u;
        this.tryCeInit();
      }
    } catch (_) {}
  }

  @Input('header-data')
  set _header_data(v: string) {
    try {
      const header = safeJsonInput<any>(v || '{}', null, 'gold-sync.parse2');
      if (header?.generalConfigData && Object.keys(header.generalConfigData).length > 0) {
        this.ceGeneralConfig = header.generalConfigData;
        this.tryCeInit();
      }
    } catch (_) {}
  }

  @Input('row-data') set _row_data(_v: string) {}
  @Input('filter-params') set _filter_params(_v: string) {}

  tabIndex = 0;
  dashboardTabHover = false;
  logTabHover = false;
  showEditModal = false;
  goldSyncDataLoading = true;
  logHistoryLoading = false;
  goldSyncData: any[] = [];
  goldSyncDataCopy: any[] = [];
  logHistoryRows: Record<string, unknown>[] = [];
  logPage = 1;
  logPageSize = 10;
  logPageLengthOptions = [10, 25, 50, 100];
  logTableSearchTerm = '';
  openTaxYears: any[] = [];
  openScenariosByTaxYear: Record<string, any[]> = {};
  groupTypes: { grp_type: string }[] = [];
  selectedGroupType: { grp_type: string } | null = null;
  groupTypeSelected = '';
  selectedTY = '';
  selectedScenario: any[] = [];
  enableTaxYearSelection = false;
  enableScenarioSelection = false;
  openScenarios: any[] = [];
  scenarioDropdownList: any[] = [];
  goldSyncJobs: { job_id: number; job_name: string; selected?: boolean }[] = [];
  goldSyncTaxYears: { tax_year: string; selected?: boolean }[] = [];
  logTaxYearList: (string | number)[] = [];
  logJobIdList: (string | number)[] = [];
  jobNamesSelectAll = false;
  taxYearSelectAll = false;
  jobNameSearchTerm = '';
  taxYearSearchTerm = '';
  showJobNameFilter = false;
  showTaxYearFilter = false;
  closeFilters = true;

  srcTypeMappings: SrcTypeMapping[] = [
    { fromData: 'API', detail: 'API' },
    { fromData: 'DS', detail: 'Data Source' },
    { fromData: 'GOLD_DS', detail: 'Gold Data Source' },
  ];
  grpTypeMappings = ['GOLD', 'CARS', 'EO_NATIVE'];
  dsNames: string[] = [];
  jobToEdit: any = null;
  newObject = false;
  invalidForm = true;
  savingEdit = false;

  showConfirmDialog = false;
  confirmModalConfig: ConfirmationModalConfig = {
    title: 'Discard changes?',
    message: 'You have unsaved changes. Are you sure you want to discard them?',
    iconClass: 'fa fa-exclamation-triangle',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Discard',
    cancelButtonText: 'Keep editing',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true,
  };
  private pendingConfirmAction: (() => void) | null = null;
  private editFormSnapshot = '';

  disableSyncAll = true;
  checkedCount = 0;
  partialSelection = false;
  fullSelection = false;
  reverseToggle = false;
  mulSelectDropDownDoShake = false;

  dashboardSearchTerm = '';
  dashboardPageSize = 10;
  dashboardPageSizeOptions = [10, 25, 50, 100];
  dashboardCurrentPage = 1;

  private ceBaseURLs: any = {};
  private ceGeneralConfig: any = {};
  private ceMode = false;
  private goldSyncBaseUrl = '';
  private wsEndpointUrl = '';
  private destroy$ = new Subject<void>();

  constructor(
    private appService: AppService,
    private globalService: GlobalService,
    private goldSyncService: GoldSyncService,
    private dialog: MatDialog,
    private snackbar: GtwSnackbarService,
    private stompMessageService: StompMessageService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    if (!this.ceMode) {
      this.goldSyncBaseUrl = this.resolveSchedulerBaseUrl();
      this.loadData();
      this.establishWebSocket();
      this.logAccessDiagnostics();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    const ssoId = this.appService.USER_SETTINGS?.user?.sso_id;
    if (this.wsEndpointUrl && ssoId) {
      this.stompMessageService.unsubscribeChannel(this.wsEndpointUrl, `/gs-notify/${ssoId}`);
    }
  }

  @HostListener('document:mousedown')
  onDocumentMouseDown(): void {
    if (this.tabIndex !== 1) return;
    if (!this.closeFilters) {
      this.closeFilters = true;
      return;
    }
    this.showJobNameFilter = false;
    this.showTaxYearFilter = false;
  }

  private tryCeInit(): void {
    this.ceMode = true;
    this.goldSyncBaseUrl = this.resolveSchedulerBaseUrl();
    if (this.goldSyncBaseUrl) {
      this.loadData();
    }
  }

  private resolveSchedulerBaseUrl(): string {
    if (this.ceMode) {
      return (
        this.ceGeneralConfig?.scheduler_api_url ||
        this.ceBaseURLs?.scheduler_api_url ||
        this.ceGeneralConfig?.gold_sync_api_url ||
        this.ceBaseURLs?.gold_sync_api_url ||
        this.ceGeneralConfig?.base_url ||
        ''
      );
    }
    return (
      this.globalService.GENERAL_CONFIG?.scheduler_api_url ||
      environment.scheduler_api_url ||
      ''
    );
  }

  /** Dev helper: compare SSO vs privileged_sso_list (see migration plan Faza 0). */
  private logAccessDiagnostics(): void {
    if (environment.production) return;
    const sso = this.appService.USER_SETTINGS?.user?.sso_id ?? '';
    const level = this.appService.USER_SETTINGS?.user?.system_access_level ?? 0;
    const priv = this.appService.SERVER_CONFIG?.configSettings?.privileged_sso_list;
    const onList = isSsoOnPrivilegedList(sso, priv);
    console.info('[GoldSync] access diagnostics', {
      sso_id: sso,
      system_access_level: level,
      tab_requires_level: 4,
      privileged_sso_list: priv,
      gold_sync_tab_visible: level >= 4 && onList,
    });
  }

  loadData(): void {
    if (!this.goldSyncBaseUrl) return;
    this.goldSyncDataLoading = true;
    this.selectedTY = '';
    this.groupTypeSelected = '';
    this.selectedScenario = [];
    this.enableScenarioSelection = false;
    this.enableTaxYearSelection = false;
    this.setOpenScenarios([]);
    this.goldSyncJobs = [];
    this.goldSyncTaxYears = [];

    this.goldSyncService.getGoldSyncApiDetails(this.goldSyncBaseUrl).pipe(takeUntil(this.destroy$)).subscribe({
      next: (response) => {
        const data = unwrapSchedulerResponse(response);
        if (isSchedulerCallFailure(data['callSuccess'])) {
          this.snackbar.openSnackBar(
            String(data['errorMessage'] || 'Failed to load Gold Sync data'),
            'gtw-snackbar--red'
          );
          this.resetDashboardData();
          return;
        }
        this.applyGoldSyncUiFromResponse((data['goldSyncUI'] as Record<string, unknown>) ?? {});
      },
      error: () => {
        this.snackbar.openSnackBar('Something went wrong when fetching table data!', 'gtw-snackbar--red');
        this.resetDashboardData();
      },
    });
  }

  private applyGoldSyncUiFromResponse(ui: any): void {
    this.goldSyncData = ui.goldSyncApiDetails ?? [];
    this.goldSyncDataCopy = [...this.goldSyncData];
    this.openTaxYears = ui.goldSyncOpenTaxYear ?? [];
    this.openScenariosByTaxYear = ui.scenariosByTaxYear ?? {};
    this.groupTypes = [];
    this.goldSyncData.forEach((data: any) => this.decorateRow(data));
    this.groupTypes = this.groupTypes.filter(
      (v, i, a) => a.findIndex((t) => t.grp_type === v.grp_type) === i
    );
    this.groupTypes.sort((a, b) => a.grp_type.localeCompare(b.grp_type));
    this.selectedGroupType = this.groupTypes.length > 0 ? this.groupTypes[0] : null;
    if (this.selectedGroupType) {
      this.groupTypeChanged(this.selectedGroupType);
    }
    this.openTaxYears.forEach((year: any) => {
      this.goldSyncTaxYears.push({ tax_year: year.tax_year, selected: false });
    });
    this.goldSyncDataLoading = false;
    this.cdr.markForCheck();
  }

  private resetDashboardData(): void {
    this.goldSyncData = [];
    this.goldSyncDataCopy = [];
    this.openTaxYears = [];
    this.groupTypes = [];
    this.goldSyncDataLoading = false;
    this.cdr.markForCheck();
  }

  private decorateRow(data: any): void {
    data.selectedGrpType = data.grp_type;
    data.jobStatusFlag = (data.job_status || 'A') === 'A';
    data.selected = false;
    const srcType = this.srcTypeMappings.find((s) => s.fromData === data.src_type);
    data.selectedSrcType = srcType ?? this.srcTypeMappings[0];
    data.srcType = data.selectedSrcType;
    if (data.src_type === 'DS') {
      data.noSrcQuery = data.src_query == null || data.src_query === '';
      data.src_details =
        data.src_query?.length > 100 ? data.src_query.substring(0, 101) + '...' : data.src_query;
      data.showTruncatedSrcQuery = data.src_query?.length > 100;
      data.showMoreNLess = data.src_query?.length > 100;
      if (data.showTruncatedSrcQuery) data.src_query_trunc = data.src_details;
    } else if (data.src_type === 'GOLD_DS') {
      data.noSrcQuery = data.src_query == null || data.src_query === '';
      data.src_details = data.src_query;
      data.showMoreNLess = true;
    } else {
      data.src_details = data.api_url;
      data.showMoreNLess = false;
      data.noSrcQuery = false;
    }
    this.checkCron(data, true);
    this.goldSyncJobs.push({ job_id: data.job_id, job_name: data.dest_tbl_name, selected: false });
    this.groupTypes.push({ grp_type: data.grp_type });
  }

  groupTypeChanged(g: { grp_type: string }): void {
    if (!g) return;
    this.groupTypeSelected = g.grp_type;
    this.selectedGroupType = g;
    if (g.grp_type === 'GOLD') {
      this.enableScenarioSelection = true;
      this.enableTaxYearSelection = true;
      this.setOpenScenarios(
        this.selectedTY ? this.openScenariosByTaxYear[this.selectedTY] ?? [] : [],
      );
    } else if (g.grp_type === 'EO_NATIVE') {
      this.enableScenarioSelection = false;
      this.enableTaxYearSelection = false;
      this.setOpenScenarios([]);
    } else {
      this.enableScenarioSelection = false;
      this.enableTaxYearSelection = true;
      this.setOpenScenarios([]);
    }
    this.goldSyncData = this.goldSyncDataCopy.filter((i) => i.grp_type === g.grp_type);
    this.checkSelection();
    this.cdr.markForCheck();
  }

  taxYearChanged(taxYear: string): void {
    this.selectedTY = taxYear ?? '';
    if (this.groupTypeSelected === 'GOLD') {
      this.enableScenarioSelection = true;
      this.selectedScenario = [];
      this.setOpenScenarios(this.openScenariosByTaxYear[this.selectedTY] ?? []);
    } else {
      this.enableScenarioSelection = false;
    }
    this.cdr.markForCheck();
  }

  onScenarioFilterResult(event: any): void {
    this.scenarioDropdownList = event?.result || [];
  }

  private setOpenScenarios(items: any[]): void {
    this.openScenarios = (items || []).map((item: any) => ({
      ...item,
      showCheckbox: true,
      role: 'checkbox',
    }));
    this.scenarioDropdownList = [...this.openScenarios];
  }

  setTab(index: number): void {
    this.tabIndex = index;
    if (index === 0) {
      this.loadData();
    } else if (index === 1) {
      this.logPage = 1;
      this.loadLogHistory();
    }
    this.cdr.markForCheck();
  }

  openEditModal(job: any): void {
    this.newObject = false;
    const sourceJob = this.resolveJobFromCopy(job);
    this.applyJobToEditFields(sourceJob);
    this.jobToEdit = sourceJob;
    this.doValidate();
    this.loadDataSources();
    this.showEditModal = true;
    this.captureEditFormSnapshot();
    this.cdr.markForCheck();
  }

  openCreateNewModal(): void {
    this.createNew();
    this.showEditModal = true;
    this.captureEditFormSnapshot();
    this.cdr.markForCheck();
  }

  requestCloseEditModal(): void {
    this.confirmDiscardIfDirty(() => this.closeEditModal());
  }

  closeEditModal(): void {
    this.showEditModal = false;
    this.editFormSnapshot = '';
    this.cdr.markForCheck();
  }

  requestCreateNewJob(): void {
    this.confirmDiscardIfDirty(() => {
      this.createNew();
      this.captureEditFormSnapshot();
    });
  }

  createNew(): void {
    this.invalidForm = true;
    this.newObject = true;
    this.jobToEdit = {
      jobStatusFlag: true,
      cron_expr: '0 0 2 * * *',
      cronDisplay: 'At 02:00 AM',
      selectedSrcType: this.srcTypeMappings[0],
      selectedGrpType: 'GOLD',
      dest_tbl_name: '',
      src_details: '',
      src_tbl_name: '',
      job_desc: '',
    };
    this.checkCron(this.jobToEdit, true);
    this.loadDataSources();
    this.cdr.markForCheck();
  }

  onJobToEditSelected(job: any): void {
    const applySelection = (): void => {
      const sourceJob = this.resolveJobFromCopy(job);
      this.applyJobToEditFields(sourceJob);
      this.jobToEdit = sourceJob;
      this.newObject = false;
      this.doValidate();
      this.captureEditFormSnapshot();
      this.cdr.markForCheck();
    };
    this.confirmDiscardIfDirty(applySelection);
  }

  private resolveJobFromCopy(job: any): any {
    if (!job) return job;
    return (
      this.goldSyncDataCopy.find((j) => j.job_id != null && j.job_id === job.job_id) ??
      this.goldSyncDataCopy.find((j) => j.dest_tbl_name === job.dest_tbl_name) ??
      job
    );
  }

  private applyJobToEditFields(job: any): void {
    job.selectedSrcType = job.srcType || job.selectedSrcType;
    job.selectedGrpType = job.grp_type;
    job.src_details =
      job.selectedSrcType?.fromData === 'DS' || job.selectedSrcType?.fromData === 'GOLD_DS'
        ? job.src_query
        : job.api_url;
  }

  changeSrcType(): void {
    if (!this.jobToEdit) return;
    if (!this.newObject) {
      this.jobToEdit.src_details =
        this.jobToEdit.selectedSrcType?.fromData === 'DS'
          ? this.jobToEdit.src_query
          : this.jobToEdit.api_url;
    }
    this.doValidate();
  }

  private serializeJobFormState(job: any): string {
    if (!job) return '';
    return JSON.stringify({
      dest_tbl_name: job.dest_tbl_name || '',
      src_details: job.src_details || '',
      src_type: job.selectedSrcType?.fromData || '',
      cron_expr: job.cron_expr || '',
      job_status: job.jobStatusFlag ? 'A' : 'I',
      grp_type: job.selectedGrpType || '',
      src_tbl_name: job.src_tbl_name || '',
      job_desc: job.job_desc || '',
      src_ds_name: job.src_ds_name || '',
    });
  }

  private captureEditFormSnapshot(): void {
    this.editFormSnapshot = this.serializeJobFormState(this.jobToEdit);
  }

  private isEditFormDirty(): boolean {
    if (!this.showEditModal || !this.jobToEdit) return false;
    return this.serializeJobFormState(this.jobToEdit) !== this.editFormSnapshot;
  }

  private confirmDiscardIfDirty(action: () => void): void {
    if (!this.isEditFormDirty()) {
      action();
      return;
    }
    this.confirmAction(
      'Discard changes?',
      'You have unsaved changes. Are you sure you want to discard them?',
      action
    );
  }

  private confirmAction(title: string, message: string, action: () => void): void {
    this.confirmModalConfig = { ...this.confirmModalConfig, title, message };
    this.pendingConfirmAction = action;
    this.showConfirmDialog = true;
    this.cdr.markForCheck();
  }

  onConfirmYes(): void {
    this.showConfirmDialog = false;
    if (this.pendingConfirmAction) {
      this.pendingConfirmAction();
      this.pendingConfirmAction = null;
    }
    this.cdr.markForCheck();
  }

  onConfirmNo(): void {
    this.showConfirmDialog = false;
    this.pendingConfirmAction = null;
    this.cdr.markForCheck();
  }

  doValidate(): void {
    const job = this.jobToEdit;
    if (
      job?.dest_tbl_name?.length > 0 &&
      job?.selectedSrcType &&
      !job?.invalidCron &&
      (job.selectedSrcType.fromData === 'API' || job.src_ds_name) &&
      job.selectedGrpType
    ) {
      this.invalidForm = false;
    } else {
      this.invalidForm = true;
    }
    this.cdr.markForCheck();
  }

  saveEdit(): void {
    if (!this.jobToEdit || this.invalidForm || this.savingEdit) return;
    this.savingEdit = true;
    const payload = { ...this.jobToEdit };
    payload.src_tbl_name = (payload.src_tbl_name || '').toUpperCase();
    payload.dest_tbl_name = (payload.dest_tbl_name || '').toUpperCase();
    payload.grp_type = (payload.selectedGrpType || payload.grp_type || 'GOLD').toUpperCase();
    payload.src_type = payload.selectedSrcType?.fromData || payload.src_type || 'API';
    payload.job_status = payload.jobStatusFlag ? 'A' : 'I';
    payload.job_desc = payload.job_desc || '';
    if (payload.src_type === 'API') {
      payload.api_url = payload.src_details || '';
    } else {
      payload.src_query = payload.src_details || '';
    }

    const req$ = this.newObject
      ? this.goldSyncService.addApiDetails(this.goldSyncBaseUrl, payload)
      : this.goldSyncService.updateApiDetails(this.goldSyncBaseUrl, payload);

    req$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (response) => {
        this.savingEdit = false;
        const data = unwrapSchedulerResponse(response);
        if (this.newObject && data['addApiDetails']) {
          this.jobToEdit.job_id = (data['addApiDetails'] as { job_id: number }).job_id;
          this.newObject = false;
          this.snackbar.openSnackBar('Api Details added successfully!', 'gtw-snackbar--green');
        } else if (!this.newObject && data['updateApiDetails']) {
          this.snackbar.openSnackBar('Api Details updated successfully!', 'gtw-snackbar--green');
        } else {
          throw new Error('callSuccess 0');
        }
        this.closeEditModal();
        this.loadData();
        this.cdr.markForCheck();
      },
      error: () => {
        this.savingEdit = false;
        this.snackbar.openSnackBar('Error processing your request, please try again!', 'gtw-snackbar--red');
        this.cdr.markForCheck();
      },
    });
  }

  private loadDataSources(): void {
    this.goldSyncService.getDataSources(this.goldSyncBaseUrl).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res: any) => {
        const raw = res?.data ?? res;
        if (Array.isArray(raw)) {
          this.dsNames = raw.map((item: any) =>
            typeof item === 'string' ? item : item?.ds_name ?? String(item)
          );
        } else {
          this.dsNames = [];
        }
        this.cdr.markForCheck();
      },
      error: () => {
        this.dsNames = [];
        this.cdr.markForCheck();
      },
    });
  }

  srcTypeChanged(row: any): void {
    if (row.selectedSrcType?.fromData === 'DS') {
      row.noSrcQuery = !row.src_query?.trim();
      row.showMoreNLess = (row.src_query?.length ?? 0) > 100;
      row.showTruncatedSrcQuery = row.showMoreNLess;
    } else if (row.selectedSrcType?.fromData === 'GOLD_DS') {
      row.noSrcQuery = !row.src_query?.trim();
      row.showMoreNLess = true;
      row.showTruncatedSrcQuery = false;
    } else {
      row.showMoreNLess = false;
      row.showTruncatedSrcQuery = false;
      row.noSrcQuery = false;
    }
    this.checkSelection();
    this.cdr.markForCheck();
  }

  getRowSourceDetailsDisplay(row: any): string {
    const srcType = row.selectedSrcType?.fromData || row.src_type;
    if (srcType === 'API') return row.api_url || '';
    if (srcType === 'DS') {
      if (row.showTruncatedSrcQuery && row.src_query_trunc) return row.src_query_trunc;
      return row.src_query || '';
    }
    return row.src_query || row.src_details || '';
  }

  showHide(row: any): void {
    row.showTruncatedSrcQuery = !row.showTruncatedSrcQuery;
    this.cdr.markForCheck();
  }

  updateApiDetails(row: any): void {
    const apiDetails = {
      job_id: row.job_id,
      src_tbl_name: row.src_tbl_name,
      dest_tbl_name: row.dest_tbl_name,
      api_url: row.api_url,
      cron_expr: row.cron_expr,
      last_run_by: row.last_run_by,
      last_run_dt: row.last_run_dt,
      updated_by: row.updated_by,
      updated_dt: row.updated_dt,
      job_status: row.jobStatusFlag ? 'A' : 'I',
      src_type: row.selectedSrcType?.fromData || row.src_type,
      src_ds_name: row.src_ds_name,
      src_query: row.src_query,
      updated_by_name: row.updated_by_name,
      jobStatusFlag: row.jobStatusFlag,
      grp_type: row.grp_type,
      job_desc: row.job_desc || '',
    };
    this.goldSyncService.updateApiDetails(this.goldSyncBaseUrl, apiDetails).pipe(takeUntil(this.destroy$)).subscribe({
      next: (response) => {
        const data = unwrapSchedulerResponse(response);
        if (isSchedulerCallSuccess(data['callSuccess']) && data['updateApiDetails']) {
          this.snackbar.openSnackBar('Api Details updated successfully!', 'gtw-snackbar--green');
        } else {
          this.snackbar.openSnackBar('Error processing your request, please try again!', 'gtw-snackbar--red');
          this.loadData();
        }
        this.checkSelection();
        this.cdr.markForCheck();
      },
      error: () => {
        this.snackbar.openSnackBar('Error processing your request, please try again!', 'gtw-snackbar--red');
        this.loadData();
        this.cdr.markForCheck();
      },
    });
  }

  checkSelection(checkboxRef?: HTMLInputElement): void {
    let activeCount = 0;
    let uncheckedCount = 0;
    for (const row of this.goldSyncData) {
      if (row.jobStatusFlag && !row.noSrcQuery) {
        activeCount++;
        if (!row.selected) uncheckedCount++;
      }
    }
    this.checkedCount = activeCount - uncheckedCount;
    this.partialSelection = false;
    this.fullSelection = false;
    this.reverseToggle = false;
    this.disableSyncAll = true;
    if (activeCount > 0 && uncheckedCount === 0) {
      this.disableSyncAll = false;
      this.fullSelection = true;
    } else if (uncheckedCount < activeCount && activeCount > 0) {
      this.disableSyncAll = false;
      this.partialSelection = true;
      this.reverseToggle = true;
    } else if (checkboxRef) {
      checkboxRef.checked = false;
    }
    this.cdr.markForCheck();
  }

  toggleSelection(event: Event): void {
    const target = event.target as HTMLInputElement;
    if (this.reverseToggle) target.checked = false;
    const checked = target.checked;
    this.goldSyncData.forEach((row) => {
      if (row.jobStatusFlag) row.selected = checked;
    });
    this.checkSelection(target);
  }

  sync(row: any): void {
    const jobIds = [row.job_id];
    const srcTypes = [row.selectedSrcType.fromData];
    if (this.groupTypeSelected !== 'EO_NATIVE') {
      this.syncApiData(jobIds, srcTypes, [row]);
    } else {
      this.syncEONativeApiData(jobIds, srcTypes, [row]);
    }
  }

  syncSelected(): void {
    if (this.disableSyncAll) return;
    const selectedJobs = this.goldSyncData.filter((d) => d.selected);
    const jobIds = selectedJobs.map((j) => j.job_id);
    const srcTypes = selectedJobs.map((j) => j.selectedSrcType.fromData);
    if (this.groupTypeSelected !== 'EO_NATIVE') {
      this.syncApiData(jobIds, srcTypes, selectedJobs);
    } else {
      this.syncEONativeApiData(jobIds, srcTypes, selectedJobs);
    }
  }

  private syncApiData(jobIds: number[], srcTypes: string[], selectedJobs: any[]): void {
    if (!this.groupTypeSelected) {
      this.snackbar.openSnackBar('Please select sync type from the dropdown!', 'gtw-snackbar--yellow');
      return;
    }
    if (!this.selectedTY) {
      this.triggerDropdownShake();
      this.snackbar.openSnackBar('Please select tax years from the dropdown!', 'gtw-snackbar--yellow');
      return;
    }
    const scenarios = this.selectedScenario.map((s) => s.scenario);
    if (this.groupTypeSelected === 'GOLD' && scenarios.length === 0) {
      this.triggerDropdownShake();
      this.snackbar.openSnackBar('Please select scenario from dropdown!', 'gtw-snackbar--yellow');
      return;
    }
    this.goldSyncService
      .syncApiData(this.goldSyncBaseUrl, this.selectedTY, jobIds, srcTypes, scenarios).pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const data = unwrapSchedulerResponse(response);
          if (isSchedulerCallSuccess(data['callSuccess'])) {
            this.snackbar.openSnackBar('Synchronization successfully initiated.', 'gtw-snackbar--green');
            selectedJobs.forEach((j) => (j.selected = false));
            this.checkSelection();
          } else {
            this.snackbar.openSnackBar('Synchronization failed to start!', 'gtw-snackbar--red');
          }
          this.cdr.markForCheck();
        },
        error: () => {
          this.snackbar.openSnackBar('Synchronization failed to start!', 'gtw-snackbar--red');
          this.cdr.markForCheck();
        },
      });
  }

  private syncEONativeApiData(_jobIds: number[], _srcTypes: string[], selectedJobs: any[]): void {
    if (!this.groupTypeSelected) {
      this.snackbar.openSnackBar('Please select sync type from the dropdown!', 'gtw-snackbar--yellow');
      return;
    }
    const clientDs = this.appService.USER_SETTINGS?.user?.client?.client_datasource;
    const job = selectedJobs[0];
    const req$ =
      job.src_details != null
        ? this.goldSyncService.syncEONativeApiData(this.goldSyncBaseUrl, job, clientDs)
        : this.goldSyncService.syncEONativeRPData(this.goldSyncBaseUrl, job, clientDs);
    req$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (response) => {
        const data = unwrapSchedulerResponse(response);
        if (isSchedulerCallSuccess(data['callSuccess'])) {
          this.snackbar.openSnackBar(
            String(data['status'] || 'Synchronization started.'),
            'gtw-snackbar--green'
          );
        } else if (isSchedulerCallFailure(data['callSuccess'])) {
          this.snackbar.openSnackBar(
            String(data['status'] || 'Synchronization failed.'),
            'gtw-snackbar--red'
          );
        } else {
          this.snackbar.openSnackBar('Something went wrong while EO object sync.', 'gtw-snackbar--red');
        }
        selectedJobs.forEach((j) => (j.selected = false));
        this.checkSelection();
        this.cdr.markForCheck();
      },
      error: () => {
        this.snackbar.openSnackBar('Synchronization failed to start!', 'gtw-snackbar--red');
        this.cdr.markForCheck();
      },
    });
  }

  private triggerDropdownShake(): void {
    this.mulSelectDropDownDoShake = true;
    setTimeout(() => {
      this.mulSelectDropDownDoShake = false;
      this.cdr.markForCheck();
    }, 350);
  }

  onTableNameClick(tableName: string): void {
    this.dialog.open(GoldSyncTableDetailsDialogComponent, {
      width: '420px',
      panelClass: 'admin-modal-panel',
      data: {
        baseUrl: this.goldSyncBaseUrl,
        tableName,
      } as GoldSyncTableDetailsDialogData,
    });
  }

  onCronHelpBtnClick(): void {
    this.dialog.open(GoldSyncCronHelpDialogComponent, {
      width: '920px',
      panelClass: 'admin-modal-panel',
    });
  }

  onCronEditorFocused(job: any): void {
    job.useDisplayVal = true;
    job.cronDisplay = job.cron_expr;
    this.cdr.markForCheck();
  }

  onCronEditorBlur(job: any): void {
    this.checkCron(job, false);
    job.useDisplayVal = false;
    if (!job.invalidCron) {
      job.cron_expr = job.cronDisplay;
      job.cronDisplay = job.readableCron;
    } else {
      job.doShake = true;
      setTimeout(() => {
        job.doShake = false;
        this.checkCron(job, true);
        this.cdr.markForCheck();
      }, 350);
    }
    this.doValidate();
    this.cdr.markForCheck();
  }

  checkCron(job: any, override: boolean): void {
    this.translateCron(job, override);
    job.invalidCron = String(job.readableCron || '').includes('Error');
  }

  private translateCron(job: any, override: boolean): void {
    if (job?.cron_expr) {
      try {
        const readableCron = cronstrue.toString(job.useDisplayVal ? job.cronDisplay : job.cron_expr);
        job.readableCron =
          readableCron.includes('undefined') ||
          readableCron.includes('NaN') ||
          (readableCron.includes('only in') && this.isYearTokenNaN(readableCron))
            ? 'Error: Invalid tokens found!'
            : readableCron;
      } catch (e) {
        job.readableCron = String(e);
      }
    } else {
      job.readableCron = 'Error: Enter a valid cron expression!';
    }
    if (override) job.cronDisplay = job.readableCron;
  }

  private isYearTokenNaN(readableCron: string): boolean {
    const pieces = readableCron.split('only in');
    const yearToken = pieces[pieces.length - 1].trim();
    const months = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
    return !months.includes(yearToken.toLowerCase()) && isNaN(Number(yearToken));
  }

  loadLogHistory(): void {
    this.logHistoryLoading = true;
    this.logHistoryRows = [];
    this.goldSyncService
      .getLogHistory(this.goldSyncBaseUrl, {
        taxYearList: this.logTaxYearList,
        jobIdList: this.logJobIdList,
        page: this.logPage,
        size: this.logPageSize,
      }).pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const { rows, errorMessage } = extractGoldSyncLogHistory(response);
          if (errorMessage) {
            this.snackbar.openSnackBar(errorMessage, 'gtw-snackbar--red');
            this.logHistoryRows = [];
          } else {
            this.logHistoryRows = rows;
          }
          this.logHistoryLoading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.logHistoryRows = [];
          this.logHistoryLoading = false;
          this.snackbar.openSnackBar('Something went wrong when fetching table data!', 'gtw-snackbar--red');
          this.cdr.markForCheck();
        },
      });
  }

  onLogPageLengthSelected(): void {
    this.logPage = 1;
    this.loadLogHistory();
  }

  flipLogPage(direction: number): void {
    if (direction === -1 && this.logPage === 1) return;
    this.logPage += direction;
    this.loadLogHistory();
  }

  logHistoryFilter(row: Record<string, unknown>): boolean {
    if (!this.logTableSearchTerm) return true;
    const term = this.logTableSearchTerm.toLowerCase();
    const jobName = row['job_name'];
    const taxYear = row['tax_year'];
    const syncCount = row['sync_count'];
    const description = row['description'];
    return (
      (jobName != null && String(jobName).toLowerCase().includes(term)) ||
      (taxYear != null && String(taxYear) === term) ||
      (syncCount != null && String(syncCount) === term) ||
      (description != null && String(description).toLowerCase().includes(term))
    );
  }

  get filteredLogHistoryRows(): Record<string, unknown>[] {
    return this.logHistoryRows.filter((row) => this.logHistoryFilter(row));
  }

  trackLogHistoryRow(_index: number, row: Record<string, unknown>): string {
    return `${row['job_name']}|${row['tax_year']}|${row['refresh_dt']}|${row['group_obj_key']}`;
  }

  openJobNameFilter(event: MouseEvent): void {
    event.stopPropagation();
    this.showTaxYearFilter = false;
    this.showJobNameFilter = true;
    this.closeFilters = false;
  }

  openTaxYearFilter(event: MouseEvent): void {
    event.stopPropagation();
    this.showJobNameFilter = false;
    this.showTaxYearFilter = true;
    this.closeFilters = false;
  }

  onFilterPanelMouseDown(event: MouseEvent): void {
    event.stopPropagation();
    this.closeFilters = false;
  }

  jobNamesFilterSelectAll(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.goldSyncJobs.forEach((j) => (j.selected = checked));
    this.cdr.markForCheck();
  }

  taxYearFilterSelectAll(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.goldSyncTaxYears.forEach((y) => (y.selected = checked));
    this.cdr.markForCheck();
  }

  searchJobName(job: { job_name: string; selected?: boolean }): boolean {
    if (job.selected) return true;
    return this.jobNameSearchTerm
      ? job.job_name.toLowerCase().includes(this.jobNameSearchTerm.toLowerCase())
      : true;
  }

  searchTaxYear(year: { tax_year: string; selected?: boolean }): boolean {
    if (year.selected) return true;
    return this.taxYearSearchTerm
      ? year.tax_year.toLowerCase().includes(this.taxYearSearchTerm.toLowerCase())
      : true;
  }

  filterByJobName(): void {
    this.showJobNameFilter = false;
    this.logJobIdList = this.goldSyncJobs.filter((j) => j.selected).map((j) => j.job_id);
    this.goldSyncTaxYears.forEach((y) => (y.selected = false));
    this.taxYearSelectAll = false;
    this.logTaxYearList = [];
    this.logPage = 1;
    this.loadLogHistory();
  }

  filterByTaxYear(): void {
    this.showTaxYearFilter = false;
    this.logTaxYearList = this.goldSyncTaxYears.filter((y) => y.selected).map((y) => y.tax_year);
    this.goldSyncJobs.forEach((j) => (j.selected = false));
    this.jobNamesSelectAll = false;
    this.logJobIdList = [];
    this.logPage = 1;
    this.loadLogHistory();
  }

  get filteredDashboardRows(): any[] {
    let rows = this.goldSyncData;
    if (this.dashboardSearchTerm) {
      const t = this.dashboardSearchTerm.toLowerCase();
      rows = rows.filter(
        (r) =>
          (r.dest_tbl_name || '').toLowerCase().includes(t) ||
          this.getRowSourceDetailsDisplay(r).toLowerCase().includes(t) ||
          (r.src_query || '').toLowerCase().includes(t) ||
          (r.updated_by_name || '').toLowerCase().includes(t)
      );
    }
    return rows;
  }

  get paginatedDashboardRows(): any[] {
    const start = (this.dashboardCurrentPage - 1) * this.dashboardPageSize;
    return this.filteredDashboardRows.slice(start, start + this.dashboardPageSize);
  }

  get dashboardTotalPages(): number {
    return Math.max(1, Math.ceil(this.filteredDashboardRows.length / this.dashboardPageSize));
  }

  get dashboardPaginationInfoText(): string {
    const filtered = this.filteredDashboardRows;
    if (!filtered.length) return '0 items';
    const start = (this.dashboardCurrentPage - 1) * this.dashboardPageSize + 1;
    const end = Math.min(this.dashboardCurrentPage * this.dashboardPageSize, filtered.length);
    return `${start}-${end} of ${filtered.length} items`;
  }

  get batchSyncTooltip(): string {
    if (this.disableSyncAll) {
      return 'Select jobs with the checkboxes, then run batch sync';
    }
    return `Sync ${this.checkedCount} selected job(s)`;
  }

  goToDashboardPage(page: number): void {
    const target = Math.min(Math.max(1, page), this.dashboardTotalPages);
    this.dashboardCurrentPage = target;
    this.cdr.markForCheck();
  }

  onDashboardPageSizeChange(size: number): void {
    this.dashboardPageSize = Number(size);
    this.dashboardCurrentPage = 1;
    this.cdr.markForCheck();
  }

  refresh(): void {
    if (this.ceMode) {
      this.requestGridUpdate.emit('refresh');
    }
    this.loadData();
  }

  private establishWebSocket(): void {
    const ssoId = this.appService.USER_SETTINGS?.user?.sso_id;
    if (!ssoId || !this.goldSyncBaseUrl) return;
    this.wsEndpointUrl = `${this.goldSyncBaseUrl}/gold-sync-websockets`;
    const channel = `/gs-notify/${ssoId}`;
    this.stompMessageService.establishConnection(this.wsEndpointUrl, () => {
      this.stompMessageService.subscribeToChannelWithCallback(
        this.wsEndpointUrl,
        channel,
        (message) => {
          try {
            const notification = safeJsonInput<any>(message.body, null, 'gold-sync.parse3');
            if (notification.consolidateMsg === 'true') {
              this.snackbar.openSnackBar(notification.notificationMessage, 'gtw-snackbar--green');
            } else if (notification.apiDetail && notification.type === 'success') {
              const updatedApiDetail = safeJsonInput<any>(notification.apiDetail, null, 'gold-sync.parse4');
              const updatedJob = this.goldSyncData.find((d) => d.job_id === updatedApiDetail.job_id);
              if (updatedJob) {
                updatedJob.last_run_dt = updatedApiDetail.last_run_dt;
                updatedJob.updated_by_name = updatedApiDetail.updated_by_name;
                this.cdr.markForCheck();
              }
            }
          } catch (e) {
            console.error('[GoldSync] websocket parse error', e);
          }
        }
      );
    });
  }
}
